import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FaqSection from "../components/FaqSection";
import { api } from "../lib/api";
import { useContent } from "../lib/content";
import { toast } from "sonner";

// Reading-type IDs (internal — kept for backend compatibility) + display labels
const READING_TYPES = [
  { id: "Tríada",
    nameKey: "reading_type_triada",     defaultName: "El Faro Interno",
    subKey:  "reading_type_triada_subtitle", defaultSub: "Sol, Luna y Ascendente" },
  { id: "Personal",
    nameKey: "reading_type_personal",   defaultName: "Las Herramientas del Navegante",
    subKey:  "reading_type_personal_subtitle", defaultSub: "Mercurio, Venus y Marte" },
  { id: "Completo",
    nameKey: "reading_type_completo",   defaultName: "La Travesía Completa",
    subKey:  "reading_type_completo_subtitle", defaultSub: "Sol, Luna, Ascendente, Mercurio, Venus y Marte" },
  { id: "Revolución Solar",
    nameKey: "reading_type_revolucion", defaultName: "Revolución Solar",
    subKey:  "reading_type_revolucion_subtitle", defaultSub: "El año astrológico que se abre con tu cumpleaños" },
];

function fmtUYU(n) { return n ? `$${n.toLocaleString("es-UY")}` : ""; }
function fmtUSD(n) { return n ? `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}` : ""; }

export default function Solicitar() {
  const { t } = useContent();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [pricing, setPricing] = useState(null);
  const [disabledTypes, setDisabledTypes] = useState([]);
  const [waEnabled, setWaEnabled] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "",
    birth_date: "", birth_time: "12:00", birth_place: "",
    plan: params.get("plan") === "session" ? "session" : "premium",
    reading_type: "Tríada",
    notes: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/config").then(({ data }) => {
      setPricing(data.pricing);
      setDisabledTypes(data.disabled_reading_types || []);
      setWaEnabled(!!data.whatsapp_enabled);
    }).catch(() => {});
    if (params.get("cancelled")) toast.error("Pago cancelado. Cuando estés lista, vuelve a intentarlo.");
  }, [params]);

  const current = disabledTypes.includes(form.reading_type)
    ? null
    : pricing?.[form.reading_type]?.[form.plan];
  const sessionCount = current?.session_count || 0;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (waEnabled) {
        // Manual payment flow — order is saved, WhatsApp opens with the request pre-written
        const { data } = await api.post("/orders/whatsapp", { ...form, origin_url: window.location.origin });
        const win = window.open(data.whatsapp_url, "_blank");
        if (!win) {
          // Popup blocked (mobile browsers) — navigate the current tab instead
          window.location.href = data.whatsapp_url;
          return;
        }
        navigate(`/lectura/${data.order_id}`);
        return;
      }
      const { data } = await api.post("/orders/create", { ...form, origin_url: window.location.origin });
      if (data.url) {
        window.location.href = data.url;
      } else {
        navigate(`/exito?order_id=${data.order_id}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear el pedido");
      setLoading(false);
    }
  };

  return (
    <div data-testid="solicitar-page" className="min-h-screen">
      <Navbar />
      <section className="pt-32 pb-20 max-w-5xl mx-auto px-6">
        <p className="overline mb-3">{t("solicitar_overline")}</p>
        <h1 className="font-display text-5xl sm:text-6xl text-white font-light tracking-tighter mb-4">
          {t("solicitar_title")}
        </h1>
        <p className="text-zinc-400 max-w-xl mb-12 leading-relaxed">{t("solicitar_subtitle")}</p>

        <form onSubmit={submit} data-testid="solicitar-form" className="space-y-10">
          {/* STEP 1 — Reading type */}
          <div>
            <p className="overline mb-4">{t("solicitar_step1_label")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {READING_TYPES.map((r) => {
                const isDisabled = disabledTypes.includes(r.id);
                return (
                  <button
                    key={r.id}
                    type="button"
                    disabled={isDisabled}
                    data-testid={`reading-type-${r.id.toLowerCase().replace(/\s/g,'-').normalize('NFD').replace(/[\u0300-\u036f]/g,'')}`}
                    onClick={() => !isDisabled && setForm({ ...form, reading_type: r.id })}
                    className={`text-left p-5 border transition relative ${
                      isDisabled
                        ? "border-white/5 opacity-50 cursor-not-allowed"
                        : form.reading_type === r.id
                          ? "border-[#C9A25E] bg-[#C9A25E]/5"
                          : "border-white/10 hover:border-white/25"
                    }`}
                  >
                    <p className="font-display text-xl text-white">{t(r.nameKey, r.defaultName)}</p>
                    <p className="text-xs text-zinc-500 mt-1">{t(r.subKey, r.defaultSub)}</p>
                    {isDisabled && (
                      <span data-testid="reading-unavailable-badge"
                        className="absolute top-3 right-3 text-[10px] tracking-[0.2em] uppercase text-zinc-500 border border-white/10 px-2 py-1">
                        No disponible
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* STEP 2 — Plan selector */}
          <div>
            <p className="overline mb-4">{t("solicitar_step2_label")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {["premium", "session"].map((planId) => {
                const planPrice = pricing?.[form.reading_type]?.[planId];
                const isSession = planId === "session";
                const sc = planPrice?.session_count || 0;
                const planLabel = isSession
                  ? (sc === 2 ? t("solicitar_plan_session_2_label") : t("solicitar_plan_session_label"))
                  : t("solicitar_plan_premium_label");
                return (
                  <button
                    key={planId}
                    type="button"
                    data-testid={`select-plan-${planId}`}
                    onClick={() => setForm({ ...form, plan: planId })}
                    className={`text-left p-5 border transition ${
                      form.plan === planId
                        ? "border-[#C9A25E] bg-[#C9A25E]/5"
                        : "border-white/10 hover:border-white/25"
                    }`}
                  >
                    <p className="font-display text-xl text-white">{planLabel}</p>
                    {planPrice ? (
                      <p className="text-sm mt-2">
                        <span className="text-[#C9A25E]">{fmtUYU(planPrice.uyu)} UYU</span>
                        <span className="text-zinc-500 mx-2">/</span>
                        <span className="text-zinc-300">{fmtUSD(planPrice.usd)} USD</span>
                      </p>
                    ) : (
                      <p className="text-xs text-zinc-500 mt-2">—</p>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* STEP 3 — Birth details */}
          <div>
            <p className="overline mb-4">{t("solicitar_step3_label")}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <label className="field-label">{t("solicitar_field_name")}</label>
                <input required data-testid="solicitar-name" className="input-field" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="field-label">{t("solicitar_field_email")}</label>
                <input required type="email" data-testid="solicitar-email" className="input-field" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <label className="field-label">{t("solicitar_field_date")}</label>
                <input required type="date" data-testid="solicitar-date" className="input-field" value={form.birth_date}
                  onChange={(e) => setForm({ ...form, birth_date: e.target.value })} />
              </div>
              <div>
                <label className="field-label">{t("solicitar_field_time")}</label>
                <input required type="time" data-testid="solicitar-time" className="input-field" value={form.birth_time}
                  onChange={(e) => setForm({ ...form, birth_time: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="field-label">{t("solicitar_field_place")}</label>
                <input required type="text" placeholder={t("solicitar_field_place_placeholder")}
                  data-testid="solicitar-place" className="input-field" value={form.birth_place}
                  onChange={(e) => setForm({ ...form, birth_place: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="field-label">{t("solicitar_field_notes")}</label>
                <textarea data-testid="solicitar-notes" rows={3} className="input-field" value={form.notes}
                  placeholder={t("solicitar_field_notes_placeholder")}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
          </div>

          {/* Total + submit */}
          <div className="p-6 border border-white/10 flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="overline mb-1">{t("solicitar_total_label")}</p>
              {current ? (
                <>
                  <p className="font-display text-3xl text-white">
                    {fmtUYU(current.uyu)} <span className="text-zinc-500 text-base">UYU</span>
                    <span className="text-zinc-600 mx-2 text-2xl">/</span>
                    {fmtUSD(current.usd)} <span className="text-zinc-500 text-base">USD</span>
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">
                    {waEnabled
                      ? "Coordinas el pago directamente conmigo por WhatsApp (Wise o Prex)"
                      : t("solicitar_total_currency_note")}
                  </p>
                </>
              ) : disabledTypes.includes(form.reading_type) ? (
                <p className="text-zinc-500">Esta lectura no está disponible en este momento ✦</p>
              ) : (
                <p className="text-zinc-500">{t("solicitar_total_empty")}</p>
              )}
            </div>
            <button type="submit" disabled={loading || !current} data-testid="solicitar-submit" className="btn-lilac">
              {loading ? t("solicitar_loading_label") : t("solicitar_submit")}
            </button>
          </div>
          <p className="text-xs text-zinc-600">
            {waEnabled ? t("solicitar_whatsapp_note") : t("solicitar_payment_note")}
          </p>
          {form.plan === "session" && (
            <p className="text-xs text-[#C9A25E]/80">
              {t("solicitar_session_note")} {sessionCount === 2 && t("solicitar_session_extra_note")}
            </p>
          )}
        </form>
      </section>
      <FaqSection />
      <Footer />
    </div>
  );
}
